/**
 * Public viewer page reached via /share/:token/viewer.
 *
 * Two media contracts arrive here, and `/share/<token>/info` describes only
 * one of them.
 *
 * Legacy sessions: `info.cameras` lists the per-camera segment directories,
 * and playback walks that camera's playlist and plays the segments
 * sequentially through a single <video>. hls.js cannot consume that playlist —
 * the segments are whole MP4s with moov-at-end, not CMAF media chunks (no
 * EXT-X-MAP), so there is nothing for it to append. See SequentialMp4Player.
 *
 * media-contract-v2 sessions: the segment walk behind `info.cameras` finds
 * nothing (their objects are content-addressed under a per-rendition prefix),
 * so an empty camera list is NOT proof the share is empty. `/share/<token>/
 * coverage` serves both contracts and is the contract-version authority; a v2
 * verdict mounts V2Player against the token's own `/share/<token>/v2/`
 * playlists. Those five routes are additionally gated by
 * `A5_V2_SHARE_TOKEN_ENABLED`; with the flag off they 404 and the player's
 * bounded retry surfaces that as an error rather than an endless spinner.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Maximize, Minimize, Share2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SequentialMp4Player } from '../components/SequentialMp4Player';
import V2Player from '../components/viewer/V2Player';
import { isSnapshotSettled } from '../components/viewer/snapshotStatus';
import { SessionCoverage } from '../types';

interface ShareInfo {
  session_id: string;
  camera_id_scope: string | null;
  cameras: string[];
  master_url: string;
  gps_summary_url: string;
  label: string | null;
  expires_at: string | null;
}

interface ShareViewerPageProps {
  tokenOverride?: string;
}

export const ShareViewerPage: React.FC<ShareViewerPageProps> = ({ tokenOverride }) => {
  const token = tokenOverride;
  const [info, setInfo] = useState<ShareInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [segmentUrls, setSegmentUrls] = useState<string[] | null>(null);
  const [activeCamera, setActiveCamera] = useState<string | null>(null);
  const [v2Coverage, setV2Coverage] = useState<SessionCoverage | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    // Mirrors `v2Coverage` state for the poll below to read without
    // retriggering this effect on every refresh.
    let latestV2Coverage: SessionCoverage | null = null;

    const probeV2Coverage = async (): Promise<SessionCoverage | null> => {
      try {
        const res = await fetch(`/share/${token}/coverage`);
        return res.ok ? ((await res.json()) as SessionCoverage) : null;
      } catch {
        return null;
      }
    };

    // A share link has no push channel — repolling is the only way an open
    // tab ever sees a healed snapshot or fresh availability bands. Stops once
    // the latest snapshot is settled; a failed fetch keeps the last good
    // coverage and retries next tick rather than disturbing a healthy player.
    const refreshV2Coverage = async () => {
      const coverage = await probeV2Coverage();
      if (cancelled || coverage?.media_contract_version !== 2) return;
      latestV2Coverage = coverage;
      setV2Coverage(coverage);
    };

    fetch(`/share/${token}/info`)
      .then((r) => {
        if (!r.ok) throw new Error(`share info ${r.status}`);
        return r.json();
      })
      .then(async (d: ShareInfo) => {
        if (cancelled) return;
        setInfo(d);
        const cam = d.cameras[0];
        if (!cam) {
          // No legacy camera directory is not "no media" — probe the
          // contract-version authority before reporting an empty share.
          const coverage = await probeV2Coverage();
          if (cancelled) return;
          if (coverage?.media_contract_version === 2) {
            latestV2Coverage = coverage;
            setV2Coverage(coverage);
            return;
          }
          setError('share has no cameras');
          return;
        }
        setActiveCamera(cam);
        const playlistUrl = `/share/${token}/${encodeURIComponent(cam)}/playlist.m3u8`;
        try {
          const res = await fetch(playlistUrl);
          if (!res.ok) {
            throw new Error(`playlist ${res.status}`);
          }
          const text = await res.text();
          const urls: string[] = [];
          for (const line of text.split(/\r?\n/)) {
            const trimmed = line.trim();
            if (!trimmed || trimmed.startsWith('#')) continue;
            const base = playlistUrl.substring(0, playlistUrl.lastIndexOf('/') + 1);
            urls.push(trimmed.startsWith('http') ? trimmed : base + trimmed);
          }
          if (!cancelled) setSegmentUrls(urls);
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'failed to load playlist');
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'failed to load share');
      });

    const id = window.setInterval(() => {
      if (latestV2Coverage && !isSnapshotSettled(latestV2Coverage)) void refreshV2Coverage();
    }, 5_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.warn('fullscreen failed', err);
    }
  };

  return (
    <div ref={containerRef} className="min-h-[100dvh] bg-bg-base p-2 text-fg sm:p-4">
      <div className="mb-2 flex items-center gap-2">
        <Share2 className="size-5 text-accent-purple" />
        <span className="font-mono text-accent-purple">Map Player · shared link</span>
        {info?.label && (
          <Badge variant="outline" className="font-mono text-fg-secondary">
            {info.label}
          </Badge>
        )}
        {info?.session_id && (
          <Badge variant="outline" className="font-mono text-fg-secondary">
            {info.session_id}
          </Badge>
        )}
        <div className="flex-1" />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <Minimize /> : <Maximize />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {error && <p className="mt-8 font-mono text-status-error">{error}</p>}

      {!info && !error && (
        <div className="mt-16 flex flex-col items-center gap-3">
          <Spinner size={28} />
          <p className="text-fg-secondary">loading share…</p>
        </div>
      )}

      {info && v2Coverage && (
        <div className="mt-4 flex h-[calc(100dvh-120px)] flex-col">
          <V2Player
            masterUrl={`/share/${token}/v2/master.m3u8`}
            audioUnion={v2Coverage.audio_union}
            videoUnion={v2Coverage.video_union}
            snapshotRev={v2Coverage.snapshot_rev ?? null}
            chrome="minimal"
            // The page owns fullscreen (it takes the header along); routing the
            // playbar's Maximise here keeps one affordance rather than nesting
            // a second, player-only one inside it.
            onMaximize={toggleFullscreen}
            maximized={isFullscreen}
            onFatalError={(detail) => {
              // Unmount rather than stack a page error on top of the player's
              // own overlay. The coverage poll above keeps running in the
              // background, so a session that recovers remounts on its own —
              // a slow, visible retry rather than a tight loop.
              setV2Coverage(null);
              setError(`playback unavailable — ${detail}`);
            }}
          />
        </div>
      )}
      {info && !v2Coverage && segmentUrls && segmentUrls.length > 0 && (
        <div className="mt-4 h-[calc(100dvh-120px)]">
          <SequentialMp4Player
            segmentUrls={segmentUrls}
            label={activeCamera ? `${activeCamera}` : null}
          />
        </div>
      )}
      {info && !v2Coverage && segmentUrls && segmentUrls.length === 0 && (
        <p className="mt-8 font-mono text-fg-secondary">this session has no segments yet</p>
      )}
    </div>
  );
};

export default ShareViewerPage;
